import React, { useEffect } from 'react';
import { Drawer, message } from 'antd';
import moment from 'moment';
import { usePatientChart } from '../context/PatientChartContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import PatientDetailPage from '../pages/PatientDetailPage';
import VitalsBanner from './VitalsBanner';

const PatientChartDrawer = ({ isChatDropdownVisible }) => { // Add isChatDropdownVisible prop
  const { activeChartId, isChartVisible, hideChart, openCharts, vitalsRefreshTrigger } = usePatientChart();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle case where patient chart state is lost after page reload
  useEffect(() => {
    // If drawer should be visible but no active chart, likely a page reload issue
    if (isChartVisible && !activeChartId && openCharts.length === 0) {
      console.warn('PatientChartDrawer: Chart state lost after reload, redirecting to patients list');
      hideChart(); // Hide the drawer
      
      // If we're on a patient detail page, extract patientId and try to recover
      const patientDetailMatch = location.pathname.match(/\/patients\/([^\/]+)/);
      if (patientDetailMatch) {
        const patientId = patientDetailMatch[1];
        message.warning('Chart state was lost after reload. Please reopen the patient chart.');
        navigate('/patients'); // Redirect to patients list
      } else {
        // We're not on a patient page, just hide drawer
        message.info('Chart closed due to page reload.');
      }
    }
  }, [isChartVisible, activeChartId, openCharts.length, hideChart, location.pathname, navigate]);

  // Helper functions (matching PatientBanner.js for consistency)
  const formatPatientName = (patient) => {
    const lastName = patient.lastName || '';
    const middleName = patient.middleName ? ` ${patient.middleName}` : '';
    const firstName = patient.firstName || '';
    return `${lastName}${middleName} ${firstName}`.trim();
  };

  const getSexAbbreviation = (sex) => {
    if (!sex) return '';
    return sex === 'Male' ? 'M' : sex === 'Female' ? 'F' : '';
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    return moment().diff(moment(dob), 'years');
  };

  // Find the active patient name for the drawer title
  const activePatient = openCharts.find(chart => chart.id === activeChartId);
  let drawerTitle = t('patient.patientChart'); // Default title

  if (activePatient) {
    // Use the same formatting as PatientBanner for consistency
    const formattedName = formatPatientName(activePatient);
    const formattedDob = activePatient.dateOfBirth ? moment(activePatient.dateOfBirth).format('DD/MM/YYYY') : 'N/A';
    const age = calculateAge(activePatient.dateOfBirth);
    const sexAbbr = getSexAbbreviation(activePatient.sex);

    // Format: "Last Name Middle Name First Name, DD/MM/YYYY, (AgeSex)" + Vitals
    const patientInfo = `${formattedName}, ${formattedDob}, (${age}${sexAbbr})`;
    drawerTitle = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span>{patientInfo}</span>
        <VitalsBanner patientId={activeChartId} compact={true} refreshTrigger={vitalsRefreshTrigger} />
      </div>
    );
  }

  return (
    <Drawer
      title={drawerTitle} // This will now use the new formatted title
      placement="top"
      closable={true}
      onClose={hideChart}
      open={isChartVisible && activeChartId !== null}
      height="calc(100vh - 64px)" // Full height minus header (64px is standard Ant Design header height)
      destroyOnClose={false}
      keyboard={true}
      maskClosable={!isChatDropdownVisible} // Conditionally set maskClosable
      style={{
        position: 'absolute',
        top: '64px', // Position it right below the header
        zIndex: 1000 // Ensure it's above other content
      }}
      bodyStyle={{
        padding: 0,
        overflow: 'auto',
        height: 'calc(100vh - 64px - 43px)' // Full height minus header and reduced drawer header (was 55px)
      }}
      headerStyle={{
        padding: '10px 24px', // Reduced vertical padding from 16px to 10px
        borderBottom: '1px solid #f0f0f0'
      }}
    >
      {activeChartId && <PatientDetailPage key={activeChartId} patientId={activeChartId} />}
    </Drawer>
  );
};

export default PatientChartDrawer;
